"use client";

import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/disclaimer";
import { computeFireProjection } from "@/lib/calculators/fire";
import { formatCompactCurrency, formatCurrency } from "@/lib/format";

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

/**
 * FIRE-/Finanzielle-Freiheit-Rechner (backlog.md -> Aktienfinder-Benchmark) - für
 * Akademie/Startseite, aktien-unabhängig, ohne Login. Rein hypothetische
 * Modellrechnung mit angenommener konstanter Rendite, siehe
 * lib/calculators/fire.ts. Gleiche Chart-Konventionen wie der bestehende
 * Zinseszins-Rechner (lineare Interpolation, Y-Achse bei 0).
 */
export function FireCalculator() {
  const [currentSavings, setCurrentSavings] = useState("10000");
  const [monthlyContribution, setMonthlyContribution] = useState("500");
  const [annualReturnPercent, setAnnualReturnPercent] = useState("7");
  const [annualExpenses, setAnnualExpenses] = useState("24000");
  const [withdrawalPercent, setWithdrawalPercent] = useState("4");

  const result = useMemo(
    () =>
      computeFireProjection({
        currentSavings: parseAmount(currentSavings),
        monthlyContribution: parseAmount(monthlyContribution),
        annualReturnRate: parseAmount(annualReturnPercent) / 100,
        annualExpenses: parseAmount(annualExpenses),
        withdrawalRate: parseAmount(withdrawalPercent) / 100,
      }),
    [currentSavings, monthlyContribution, annualReturnPercent, annualExpenses, withdrawalPercent],
  );

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">FIRE-Rechner</span>
        <p className="mt-1 text-xs text-ink-secondary">
          Rein hypothetische Modellrechnung, wann ein angespartes Kapital laut der
          klassischen Entnahmerate-Faustregel die eigenen jährlichen Ausgaben dauerhaft
          decken könnte - keine Prognose für eine konkrete Aktie oder einen konkreten ETF
          und keine individuelle Finanzplanung.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Aktuelles Vermögen (€)
          <Input
            type="number"
            min={0}
            value={currentSavings}
            onChange={(e) => setCurrentSavings(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Sparrate/Monat (€)
          <Input
            type="number"
            min={0}
            value={monthlyContribution}
            onChange={(e) => setMonthlyContribution(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Angenommene Rendite (%/Jahr)
          <Input
            type="number"
            value={annualReturnPercent}
            onChange={(e) => setAnnualReturnPercent(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Jährliche Ausgaben im Ruhestand (€)
          <Input
            type="number"
            min={0}
            value={annualExpenses}
            onChange={(e) => setAnnualExpenses(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Entnahmerate (%/Jahr)
          <select
            value={withdrawalPercent}
            onChange={(e) => setWithdrawalPercent(e.target.value)}
            className="rounded-md border border-hairline bg-card px-2 py-1.5 text-sm text-ink"
          >
            {["3", "3.5", "4", "4.5", "5"].map((r) => (
              <option key={r} value={r}>
                {r} %
              </option>
            ))}
          </select>
        </label>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
            <StatTile label="Zielkapital" value={formatCurrency(result.fireNumber, "EUR")} />
            <StatTile
              label="Jahre bis zum Zielkapital"
              value={result.yearsToFire !== undefined ? `${result.yearsToFire} Jahre` : "über 60 Jahre"}
            />
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="fireBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C6A15B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C6A15B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
                <XAxis
                  dataKey="year"
                  tickFormatter={(y) => `${y}J`}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, "auto"]}
                  tickFormatter={(v) => formatCompactCurrency(v, "EUR")}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(Number(value), "EUR"), "Vermögen"]}
                  labelFormatter={(y) => `Jahr ${y}`}
                />
                <ReferenceLine
                  y={result.fireNumber}
                  stroke="#16265C"
                  strokeDasharray="4 4"
                  label={{ value: "Zielkapital", position: "insideTopLeft", fontSize: 11, fill: "#16265C" }}
                />
                <Area type="linear" dataKey="balance" stroke="#C6A15B" strokeWidth={2} fill="url(#fireBalance)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[0.65rem] text-muted-foreground">
            Zielkapital = jährliche Ausgaben ÷ Entnahmerate (Standard-Faustregel, z. B. 25x
            bei 4 %). Die gestrichelte Linie markiert dieses Zielkapital im Zeitverlauf.
          </p>
        </>
      )}

      <Disclaimer />
    </div>
  );
}
